import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { salesAPI, filesAPI, downloadFile, formatError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const SalesSheet = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [headers] = useState([
    'Product ID',
    'Product Name',
    'Quantity Sold',
    'Price',
    'Revenue',
    'Sale Date'
  ]);

  // Load sales data on component mount
  useEffect(() => {
    loadSalesData();
  }, []);

  const loadSalesData = async () => {
    try {
      const response = await salesAPI.getAll();
      setSalesData(response.data);
    } catch (error) {
      toast.error('Failed to load sales data');
      console.error('Load sales error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save functionality
  const autoSave = useCallback(async (updatedData) => {
    setSaving(true);
    try {
      // Prepare batch update data
      const updates = updatedData.map(item => ({
        id: item.id,
        data: {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity_sold: parseInt(item.quantity_sold) || 0,
          price: parseFloat(item.price) || null,
          custom_headers: item.custom_headers || {}
        }
      }));

      await salesAPI.batchUpdate({
        sheet_type: 'sales',
        updates: updates
      });

      toast.success('Changes saved automatically', { duration: 2000 });
    } catch (error) {
      toast.error('Auto-save failed');
      console.error('Auto-save error:', error);
    } finally {
      setSaving(false);
    }
  }, []);

  // Handle cell editing
  const handleCellEdit = (rowIndex, field, value) => {
    const updatedData = [...salesData];

    if (field === 'quantity_sold' || field === 'price') {
      const numValue = field === 'quantity_sold' ? parseInt(value) || 0 : parseFloat(value) || 0;
      updatedData[rowIndex][field] = numValue;

      // Auto-calculate revenue
      if (updatedData[rowIndex].quantity_sold && updatedData[rowIndex].price) {
        updatedData[rowIndex].revenue = updatedData[rowIndex].quantity_sold * updatedData[rowIndex].price;
      }
    } else {
      updatedData[rowIndex][field] = value;
    }

    setSalesData(updatedData);

    // Trigger auto-save after a delay
    setTimeout(() => {
      autoSave(updatedData);
    }, 1000);
  };

  // Add new row
  const addNewRow = async () => {
    try {
      const newSale = {
        product_id: '',
        product_name: '',
        quantity_sold: 0,
        price: 0,
        revenue: 0
      };

      const response = await salesAPI.create(newSale);
      setSalesData([...salesData, response.data]);
      toast.success('New row added');
    } catch (error) {
      toast.error('Failed to add new row');
    }
  };

  // Delete selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.size === 0) {
      toast.error('No rows selected');
      return;
    }

    try {
      const deletePromises = Array.from(selectedRows).map(id =>
        salesAPI.delete(id)
      );

      await Promise.all(deletePromises);

      setSalesData(salesData.filter(item => !selectedRows.has(item.id)));
      setSelectedRows(new Set());
      toast.success(`${selectedRows.size} row(s) deleted`);
    } catch (error) {
      toast.error('Failed to delete rows');
    }
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      toast.loading('Importing file...', { id: 'file-upload' });

      const response = await filesAPI.uploadSales(file);

      toast.success(
        `${response.data.records_imported} records imported successfully`,
        { id: 'file-upload' }
      );

      // Reload data
      loadSalesData();
    } catch (error) {
      toast.error(formatError(error), { id: 'file-upload' });
    }

    // Reset file input
    event.target.value = '';
  };

  // Export data
  const handleExport = async (format) => {
    try {
      toast.loading(`Exporting ${format.toUpperCase()}...`, { id: 'export' });

      const response = await filesAPI.exportData({
        sheet_type: 'sales',
        format: format
      });

      const filename = `sales_export_${new Date().toISOString().split('T')[0]}.${format}`;
      downloadFile(response.data, filename);

      toast.success('Export completed', { id: 'export' });
    } catch (error) {
      toast.error('Export failed', { id: 'export' });
    }
  };

  // Download template
  const downloadTemplate = async () => {
    try {
      const response = await filesAPI.downloadTemplate('sales');
      downloadFile(response.data, 'sales_template.csv');
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Row selection
  const toggleRowSelection = (id) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedRows(newSelection);
  };

  // Select all rows
  const toggleSelectAll = () => {
    if (selectedRows.size === salesData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(salesData.map(item => item.id)));
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading sales data..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Sheet</h1>
          <p className="text-gray-600">Manage your sales transactions in an Excel-like interface</p>
        </div>

        {saving && (
          <div className="flex items-center text-blue-600">
            <LoadingSpinner size="sm" text="Auto-saving..." center={false} />
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          {/* Add Row */}
          <button
            onClick={addNewRow}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Row
          </button>

          {/* Delete Selected */}
          <button
            onClick={deleteSelectedRows}
            disabled={selectedRows.size === 0}
            className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete Selected ({selectedRows.size})
          </button>

          {/* File Upload */}
          <label className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Import File
            <input
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
            />
          </label>

          {/* Export Dropdown */}
          <div className="relative inline-block text-left">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleExport(e.target.value);
                  e.target.value = '';
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <option value="">Export As...</option>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="csv">CSV (.csv)</option>
            </select>
          </div>

          {/* Download Template */}
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Template
          </button>
        </div>
      </div>

      {/* Excel-like Data Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full excel-grid">
            <thead>
              <tr className="bg-gray-50">
                <th className="excel-header-cell w-12">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === salesData.length && salesData.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                {headers.map((header, index) => (
                  <th key={index} className="excel-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {salesData.map((row, rowIndex) => (
                <tr key={row.id} className={selectedRows.has(row.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                  <td className="excel-cell w-12">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(row.id)}
                      onChange={() => toggleRowSelection(row.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>

                  <td className="excel-cell">
                    <input
                      type="text"
                      value={row.product_id || ''}
                      onChange={(e) => handleCellEdit(rowIndex, 'product_id', e.target.value)}
                      className="w-full bg-transparent border-0 outline-none p-1"
                      placeholder="Auto-generated"
                    />
                  </td>

                  <td className="excel-cell">
                    <input
                      type="text"
                      value={row.product_name || ''}
                      onChange={(e) => handleCellEdit(rowIndex, 'product_name', e.target.value)}
                      className="w-full bg-transparent border-0 outline-none p-1"
                      placeholder="Product name"
                    />
                  </td>

                  <td className="excel-cell">
                    <input
                      type="number"
                      value={row.quantity_sold || ''}
                      onChange={(e) => handleCellEdit(rowIndex, 'quantity_sold', e.target.value)}
                      className="w-full bg-transparent border-0 outline-none p-1"
                      placeholder="0"
                      min="0"
                    />
                  </td>

                  <td className="excel-cell">
                    <input
                      type="number"
                      step="0.01"
                      value={row.price || ''}
                      onChange={(e) => handleCellEdit(rowIndex, 'price', e.target.value)}
                      className="w-full bg-transparent border-0 outline-none p-1"
                      placeholder="0.00"
                      min="0"
                    />
                  </td>

                  <td className="excel-cell bg-gray-50">
                    <span className="text-gray-700">
                      ${(row.revenue || 0).toFixed(2)}
                    </span>
                  </td>

                  <td className="excel-cell bg-gray-50">
                    <span className="text-gray-700 text-sm">
                      {row.sale_date ? new Date(row.sale_date).toLocaleDateString() : 'Today'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Total Sales</div>
          <div className="text-2xl font-bold text-gray-900">{salesData.length}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Total Revenue</div>
          <div className="text-2xl font-bold text-gray-900">
            ${salesData.reduce((sum, item) => sum + (item.revenue || 0), 0).toFixed(2)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-600">Unique Products</div>
          <div className="text-2xl font-bold text-gray-900">
            {new Set(salesData.map(item => item.product_id)).size}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSheet;